import type { HierarcicalObjectReference, ObjectId } from "@novorender/data-js-api";
import { useCallback, useRef } from "react";

import { Events, track } from "apis/procore";
import { useAppDispatch, useAppSelector, useAppStore } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { hiddenActions, useDispatchHidden } from "contexts/hidden";
import { highlightCollectionsActions, useDispatchHighlightCollections } from "contexts/highlightCollections";
import { highlightActions, useDispatchHighlighted } from "contexts/highlighted";
import {
    GroupStatus,
    type ObjectGroup,
    objectGroupsActions,
    useDispatchObjectGroups,
    useLazyObjectGroups,
} from "contexts/objectGroups";
import { selectionBasketActions, useDispatchSelectionBasket } from "contexts/selectionBasket";
import { shownActions, useDispatchShown } from "contexts/shown";
import { byProperty, type ObjectDataAccess } from "dataAccess";
import { PUBLIC_ID_PROPERTY } from "dataAccess/constants";
import { isInstanceId } from "features/bimIndex/idTranslation";
import { renderActions } from "features/render";
import { DEFAULT_FLY_TIME } from "features/render/hooks/useHandleCameraState";
import { viewpointCameraToCadCamera } from "features/render/utils";
import { useCancelCurrentModeChanges } from "features/toolMode/useCancelCurrentModeChanges";
import { useAbortController } from "hooks/useAbortController";
import { useFillGroupIds } from "hooks/useFillGroupIds";
import { usePdfObjectIds } from "hooks/useObjectTreeHidden";
import { explorerActions, selectIsProcoreMobileDesign } from "slices/explorer";
import { ProcoreToolMode, selectActiveToolMode } from "slices/procoreUx";
import { telemetry, type ViewpointSource } from "telemetry";
import { log } from "utils/log";
import { isLeafNode } from "utils/objectPath";
import { SelectionSet } from "utils/selectionSet";

import { type Camera, type SetViewpointOptions, type SetViewpointPayload } from "./types";

export interface UseSetViewpointOptions extends SetViewpointOptions {
    /** When true, the applied camera also becomes the initial camera. */
    setAsInitial?: boolean;
    /**
     * Hide every PDF/2D sheet instead of applying the viewpoint to them.
     * @default false
     */
    hidePdfs?: boolean;
}

export const PDF_DISCOVERY_TIMEOUT_MS = 10_000;

// ---- Hook ----

export function useSetViewpoint() {
    const {
        state: { view, webviewer, dataAccess, pdfObjectDiscovery },
    } = useExplorerGlobals();
    const dispatch = useAppDispatch();
    const cancelModeChanges = useCancelCurrentModeChanges();
    const activeToolMode = useAppSelector(selectActiveToolMode);
    // Read via the store rather than a selector: callers like useHandleInit invoke this through a
    // ref, which can run a callback captured before the render that would refresh a selector value.
    const store = useAppStore();
    const dispatchHidden = useDispatchHidden();
    const dispatchHighlighted = useDispatchHighlighted();
    const dispatchHighlightCollections = useDispatchHighlightCollections();
    const dispatchSelectionBasket = useDispatchSelectionBasket();
    const dispatchShown = useDispatchShown();
    const objectGroups = useLazyObjectGroups();
    const dispatchObjectGroups = useDispatchObjectGroups();
    const fillGroupsIds = useFillGroupIds();
    const pdfObjectIds = usePdfObjectIds();
    const pdfObjectIdsRef = useRef({ ids: pdfObjectIds, promise: pdfObjectDiscovery.objectIdsPromise });
    pdfObjectIdsRef.current = { ids: pdfObjectIds, promise: pdfObjectDiscovery.objectIdsPromise };

    const isSectionMode = activeToolMode === ProcoreToolMode.Section;

    const [abortController, abort] = useAbortController();

    return useCallback(
        async (
            requestedPayload: SetViewpointPayload,
            source: ViewpointSource = "user",
            options?: UseSetViewpointOptions,
        ) => {
            const ignoreUndefined = options?.ignoreUndefined ?? false;
            const hidePdfs = options?.hidePdfs ?? false;
            if (!view || !webviewer || !dataAccess) {
                throw new ViewpointError(
                    "useSetViewpoint: viewer is not ready (missing view, webviewer or dataAccess).",
                    false,
                );
            }

            // Mobile has no ortho toggle (BM-12544), so an ortho viewpoint is flown to as a
            // perspective one: the projection is what mobile can't offer, not the location.
            const payloadWithCamera =
                requestedPayload.camera && selectIsProcoreMobileDesign(store.getState())
                    ? { ...requestedPayload, camera: withoutOrthoProjection(requestedPayload.camera) }
                    : requestedPayload;
            // TODO(BM-14136): Restore lasers from the viewpoint again once laser is available in the UI.
            // const payload = cameraPayload;
            const payload = withoutLasers(payloadWithCamera);

            if (source === "user" && isSectionMode) {
                cancelModeChanges();
            }

            abort();
            const abortSignal = abortController.current.signal;
            dispatch(explorerActions.setIsApplyingViewpoint(true));

            // Wait for handlers to pick up proper isApplyingViewpoint before starting changing anything
            await new Promise((resolve) => setTimeout(resolve));

            const span = telemetry.startViewpointLoad(source);
            try {
                const applyViewpoint = async () => {
                    const { visibility, groups } = payload;
                    const allPublicIds = collectPublicIds(payload);
                    const hasExceptions = Boolean(
                        visibility?.exceptions?.object_ids?.length || visibility?.exceptions?.subtree_ids?.length,
                    );
                    const [publicIdsToObjectIds, subtreeExceptions, topLevelContainerIds] = await Promise.all([
                        webviewer.publicIdsToObjectIdsMap(Array.from(allPublicIds)),
                        expandSubtreeExceptions({
                            subtreeIds: visibility?.exceptions?.subtree_ids,
                            dataAccess,
                            abortSignal,
                        }),
                        hasExceptions ? listTopLevelContainerIds({ dataAccess, abortSignal }) : undefined,
                    ]);
                    abortSignal.throwIfAborted();

                    const objectExceptions = (visibility?.exceptions?.object_ids ?? [])
                        .map((id) => publicIdsToObjectIds.get(id))
                        .filter(Boolean) as number[];
                    const rawVisibilityExceptions = subtreeExceptions.length
                        ? [...objectExceptions, ...subtreeExceptions]
                        : objectExceptions;
                    // Some staging/BCF data resolves a handful of unrelated exception public ids onto
                    // a single top-level file/container's own internal id (a data/resolution artifact,
                    // not a genuine "hide this whole file" intent - see BM-10786). Writing that bare
                    // container id into the hidden set has no visual effect by itself (container ids
                    // carry no geometry), but it breaks the invariant the object tree's eye icon relies
                    // on for folder rows ("own id hidden" implies "whole subtree hidden"), so it shows
                    // a false EyeOff on an otherwise fully-visible top-level branch. Drop any such
                    // container ids here rather than expanding them, since expanding into their real
                    // descendants would actually hide the whole file's geometry - which is not what
                    // happens today and not the reported behavior for affected models.
                    const appVisibilityExceptions = SelectionSet.fromIds(
                        excludeTopLevelContainerExceptions(rawVisibilityExceptions, topLevelContainerIds),
                    );
                    // Majority of RTK stored state is restored in corresponding slices reacting to setViewpoint action.
                    dispatch(renderActions.setViewpoint(payload, view, publicIdsToObjectIds, { ignoreUndefined }));

                    // When setAsInitial is requested, update initialCamera to match this viewpoint.
                    if (options?.setAsInitial && payload.camera) {
                        dispatch(renderActions.setInitialCamera(viewpointCameraToCadCamera(payload.camera)));
                    }

                    // Selection. The viewpoint's own `selection` is deprecated and deliberately
                    // ignored, but a full (non-partial) apply still starts from a clean selection.
                    // `mainObjects` alongside `mainObject`: the object tree selects rows from the
                    // set, and reads a cleared primary next to a populated set as a selection whose
                    // primary was deselected — so its rows would survive the apply.
                    // Shown is swept here rather than with the rest of visibility below, for the
                    // same reason as the highlight collections: it is highlight state a viewpoint
                    // does not carry, so anything left in it would keep rendering at natural
                    // appearance against the new selection.
                    if (!ignoreUndefined) {
                        dispatchHighlighted(highlightActions.setIds([]));
                        dispatchHighlightCollections(highlightCollectionsActions.clearAll());
                        dispatchShown(shownActions.setIds([]));
                        dispatch(renderActions.setMainObject(undefined));
                        dispatch(renderActions.setMainObjects([]));
                    }

                    // Visibility
                    // With `hidePdfs`, every PDF sheet is hidden and isolation leaves them all out of the
                    // basket. The camera is already on its way, so only visibility waits for discovery. If
                    // it fails or times out, sheets can't be told apart from other objects, so the viewpoint
                    // decides for them too. Without `sheetIds` the viewpoint applies as-is.
                    const pdfObjects = pdfObjectIdsRef.current;
                    const sheetIds = hidePdfs
                        ? (pdfObjects.ids ??
                          (pdfObjects.promise && (visibility || !ignoreUndefined)
                              ? await waitForPdfObjectIds(pdfObjects.promise, abortSignal)
                              : undefined))
                        : undefined;
                    abortSignal.throwIfAborted();
                    const hiddenPdfIds = sheetIds ?? SelectionSet.EMPTY;
                    if (visibility) {
                        if (visibility.default_visibility) {
                            dispatchHidden(hiddenActions.setIds(appVisibilityExceptions.withAdded(hiddenPdfIds)));
                            dispatchSelectionBasket(selectionBasketActions.set([]));
                        } else {
                            dispatchHidden(hiddenActions.setIds(hiddenPdfIds));
                            dispatchSelectionBasket(
                                selectionBasketActions.set(appVisibilityExceptions.withRemoved(sheetIds ?? [])),
                            );
                        }
                        dispatchHidden(hiddenActions.setToggleSnapshot([]));
                    } else if (!ignoreUndefined) {
                        dispatchHidden(hiddenActions.setIds(hiddenPdfIds));
                        dispatchHidden(hiddenActions.setToggleSnapshot([]));
                        dispatchSelectionBasket(selectionBasketActions.set([]));
                    }

                    // Groups
                    if (groups || !ignoreUndefined) {
                        const viewpointObjectGroups = buildUpdatedGroups(objectGroups.current, groups ?? []);
                        await fillGroupsIds(viewpointObjectGroups.filter((g) => g.status !== GroupStatus.None));
                        abortSignal.throwIfAborted();
                        dispatchObjectGroups(objectGroupsActions.set(viewpointObjectGroups));
                    }

                    // TODO markup

                    // Wait for the state updates to apply and add a little buffer.
                    // Otherwise useHandleCameraMoved might fire viewer-interaction undesiredly.
                    if (payload.camera) {
                        const flyTime = payload.cameraFlyTime ?? DEFAULT_FLY_TIME;
                        await new Promise((resolve) => setTimeout(resolve, flyTime + 100));
                    } else {
                        await new Promise((resolve) => setTimeout(resolve));
                    }

                    abortSignal.throwIfAborted();

                    dispatch(
                        explorerActions.setAppliedViewpoint({
                            viewpoint: {
                                id: payload.id,
                                name: payload.name,
                            },
                            appliedBy: source === "user" ? "set-viewpoint" : "initialization",
                        }),
                    );
                    // If signal is aborted - no need to reset isApplyingViewpoint,
                    // because signal is aborted only when setViewpoint is called again,
                    // so the new setViewpoint call will reset isApplyingViewpoint.
                    dispatch(explorerActions.setIsApplyingViewpoint(false));

                    void track(Events.viewpoint.restored, getViewpointRestoredProperties(payload));
                };

                await applyViewpoint();

                telemetry.endViewpointLoad(span, true);
            } catch (e) {
                telemetry.endViewpointLoad(span, false, e instanceof Error ? e.message : String(e));
                if (!abortSignal.aborted) {
                    dispatch(explorerActions.setIsApplyingViewpoint(false));
                }
                throw e;
            }
        },
        [
            view,
            webviewer,
            dataAccess,
            isSectionMode,
            abort,
            abortController,
            dispatch,
            cancelModeChanges,
            dispatchHighlighted,
            dispatchHighlightCollections,
            dispatchHidden,
            dispatchSelectionBasket,
            dispatchShown,
            objectGroups,
            fillGroupsIds,
            dispatchObjectGroups,
            store,
        ],
    );
}

export class ViewpointError extends Error {
    constructor(
        message?: string,
        public readonly ready = true,
    ) {
        super(message);
        this.name = "ViewpointError";
        // Maintains proper stack trace (only in V8, e.g. Node.js/Chrome)
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, ViewpointError);
        }
    }
}

// ---- Helper functions ----

/**
 * @returns The sheet ids, or `undefined` if discovery fails, takes longer than {@link PDF_DISCOVERY_TIMEOUT_MS},
 * or `abortSignal` fires.
 */
async function waitForPdfObjectIds(
    promise: Promise<SelectionSet>,
    abortSignal: AbortSignal,
): Promise<SelectionSet | undefined> {
    const signal = AbortSignal.any([abortSignal, AbortSignal.timeout(PDF_DISCOVERY_TIMEOUT_MS)]);
    if (signal.aborted) {
        return undefined;
    }
    // Resolves rather than rejects: once `promise` wins the race, a later abort would go unhandled.
    const aborted = new Promise<undefined>((resolve) => {
        signal.addEventListener("abort", () => resolve(undefined), { once: true });
    });
    try {
        return await Promise.race([promise, aborted]);
    } catch {
        return undefined;
    }
}

/**
 * Rebuilds an orthographic viewpoint camera as a perspective one at the same eye position and
 * orientation. Perspective cameras are returned untouched.
 */
function withoutOrthoProjection(camera: Camera): Camera {
    // Discriminate on "perspective" so this agrees with viewpointCameraToCadCamera, which treats
    // anything that isn't perspective as ortho.
    if ("perspective" in camera) {
        return camera;
    }

    const { position, direction, camera_up, aspect_ratio } = camera.orthogonal;
    // Ortho has no fov to carry over; 60 degrees matches the scene's default camera.
    return { perspective: { position, direction, camera_up, aspect_ratio, fov: 60 } };
}

function withoutLasers(payload: SetViewpointPayload): SetViewpointPayload {
    if (!payload.measurements?.lasers) {
        return payload;
    }

    const { lasers: _lasers, ...measurements } = payload.measurements;
    return { ...payload, measurements };
}

/**
 * Every object under the `subtreeIds` roots, the roots included. Ids that are not instance
 * ids are skipped, and a failed or short expansion answers empty rather than failing the
 * viewpoint.
 *
 * @throws When `abortSignal` fires, since an aborted expansion answers empty.
 */
async function expandSubtreeExceptions({
    subtreeIds,
    dataAccess,
    abortSignal,
}: {
    subtreeIds: string[] | undefined;
    dataAccess: ObjectDataAccess;
    abortSignal: AbortSignal;
}): Promise<Uint32Array> {
    // One value `objects/expand` cannot read as an instance id 422s the whole request.
    const valid = subtreeIds?.filter(isInstanceId) ?? [];
    if (!valid.length) {
        return new Uint32Array();
    }
    try {
        const ids = await dataAccess.findIdsByProperties(byProperty(PUBLIC_ID_PROPERTY, valid), {
            includeDescendants: true,
            // A short subtree would hide only part of what was saved hidden.
            requireWhole: true,
            signal: abortSignal,
        });
        abortSignal.throwIfAborted();
        return ids;
    } catch (e) {
        abortSignal.throwIfAborted();
        log.viewpoints.warn("could not expand the subtree exceptions; skipping them", e);
        return new Uint32Array();
    }
}

/**
 * The ids of the top-level (root) files/containers, which the visibility exceptions drop.
 * `undefined` when the root can't be listed, so the exceptions apply unfiltered.
 *
 * Only the root level is checked (one cheap root listing, regardless of how many
 * exception ids there are) rather than classifying every exception id, since that's
 * the scope of the known issue (BM-10786) and classifying every exception id
 * individually would mean one metadata fetch per id - too expensive for viewpoints
 * with large exception lists.
 *
 * @throws When `abortSignal` fires, rather than answering `undefined`: an aborted
 * listing is not evidence that there are no container ids to drop.
 */
async function listTopLevelContainerIds({
    dataAccess,
    abortSignal,
}: {
    dataAccess: ObjectDataAccess;
    abortSignal: AbortSignal;
}): Promise<Set<ObjectId> | undefined> {
    let topLevelNodes: HierarcicalObjectReference[];
    try {
        topLevelNodes = await Array.fromAsync(dataAccess.getChildren(undefined, abortSignal));
    } catch {
        // An abort is not a listing failure, and the two arrive here in different
        // shapes: the object-db backend passes `db.search`'s AbortError straight
        // through, so it lands in this catch.
        abortSignal.throwIfAborted();
        // Best effort - if the root can't be listed for some reason, fall back to
        // the unfiltered exceptions rather than failing the whole viewpoint apply.
        return undefined;
    }
    // An index backend honors the ObjectDataAccess contract instead and answers an
    // abort by yielding nothing, which is indistinguishable from a model with no
    // top-level containers unless the signal is re-checked.
    abortSignal.throwIfAborted();

    return new Set(topLevelNodes.filter((node) => !isLeafNode(node)).map((node) => node.id));
}

/** `ids` without the {@link listTopLevelContainerIds} containers. */
function excludeTopLevelContainerExceptions(ids: number[], containerIds: Set<ObjectId> | undefined): number[] {
    return containerIds?.size ? ids.filter((id) => !containerIds.has(id)) : ids;
}

function collectPublicIds(payload: SetViewpointPayload): Set<string> {
    const { visibility, measurements } = payload;

    const pointLineObjectIds = (measurements?.point_line?.point_lines ?? [])
        .flat()
        .map((p) => p.object_id)
        .filter((id): id is string => id !== undefined);

    const measureObjectIds = (measurements?.measure?.entities ?? [])
        .flat()
        .map((e) => e.object_id)
        .filter((id): id is string => id !== undefined);

    return new Set([...(visibility?.exceptions?.object_ids ?? []), ...pointLineObjectIds, ...measureObjectIds]);
}

function buildUpdatedGroups(
    currentGroups: ObjectGroup[],
    viewpointGroups: SetViewpointPayload["groups"],
): ObjectGroup[] {
    return currentGroups.map((group) => {
        const viewpointGroup = (viewpointGroups ?? []).find((g) => g.id === group.id);

        let status = GroupStatus.None;
        if (viewpointGroup) {
            switch (viewpointGroup.status) {
                case "none":
                    break;
                case "selected":
                    status = GroupStatus.Selected;
                    break;
                case "hidden":
                    status = GroupStatus.Hidden;
                    break;
                case "frozen":
                    status = GroupStatus.Frozen;
                    break;
            }
        }

        return { ...group, status };
    });
}

/** Pure derivation of analytics properties when a viewpoint is restored/applied. */
export function getViewpointRestoredProperties(payload: SetViewpointPayload) {
    return {
        has_visibility: Boolean(payload.visibility),
        has_groups: Boolean(payload.groups?.length),
        has_clipping: Boolean(payload.clipping?.enabled),
        has_measurements: Boolean(payload.measurements),
    };
}
